'use client'

import {
  Bookmark,
  CheckCircle2,
  Droplets,
  Leaf,
  RefreshCw,
  Shield,
  Sparkles,
  SunMedium,
  type LucideIcon,
} from 'lucide-react'

import {
  BeautyFrame,
  BeautyKicker,
  FullBleedImage,
  SafeText,
  beautyCream,
  beautyInk,
  beautyIvory,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/educational_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/educational_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/educational_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/educational_04.jpg',
]

const total = 5

const fallbacks = [
  {
    eyebrow: 'Skincare class',
    headline: 'Los 3 pasos que cambiaron mi piel',
    subheadline:
      'Un ritual simple para una piel más luminosa, calmada y saludable.',
    body: 'Una guía práctica para entender qué hacer primero y por qué importa.',
    cta: 'Guardar rutina',
    steps: [
      {
        label: '01',
        title: 'Limpia suavemente',
        text: 'Empieza con una base limpia y equilibrada.',
      },
      {
        label: '02',
        title: 'Hidrata diario',
        text: 'Nutre y ayuda a retener la humedad.',
      },
      {
        label: '03',
        title: 'Protege siempre',
        text: 'Usa SPF cada mañana, sin excepciones.',
      },
    ],
  },
  {
    eyebrow: 'Step 1',
    headline: 'Limpia sin dejar la piel tirante',
    subheadline:
      'Una limpieza suave retira impurezas sin dañar la barrera de tu piel.',
    body:
      'Piensa en limpiar, no en dejar la piel “rechinante”. Tu piel debe sentirse cómoda después.',
    proof: 'Suave hoy, saludable mañana.',
  },
  {
    eyebrow: 'Step 2',
    headline: 'Hidrata con intención',
    subheadline:
      'La hidratación ayuda a mantener la piel cómoda, flexible y con mejor apariencia.',
    body:
      'Usa una textura que tu piel pueda sostener. La constancia importa más que la cantidad.',
    proof: 'El agua no basta. Hay que sellar la hidratación.',
  },
  {
    eyebrow: 'Step 3',
    headline: 'Protege cada mañana',
    subheadline:
      'El protector solar es tu escudo diario contra manchas, envejecimiento prematuro y daño ambiental.',
    body:
      'SPF no es negociable. Es el paso que sostiene el resto de tu rutina.',
    proof: 'SPF hoy, glow mañana.',
  },
  {
    eyebrow: 'Your glow',
    headline: 'Checklist',
    subheadline:
      'Guarda esta rutina antes de complicar tu skincare.',
    body:
      'La constancia le gana a la intensidad.',
    result:
      'Pasos simples. Resultados sostenibles.',
    cta: 'Guardar rutina',
    steps: [
      {
        label: '01',
        title: 'Limpia suavemente',
        text: 'Empieza con una base limpia y equilibrada.',
      },
      {
        label: '02',
        title: 'Hidrata diario',
        text: 'Nutre y ayuda a retener la humedad.',
      },
      {
        label: '03',
        title: 'Protege siempre',
        text: 'SPF cada mañana, sin excepciones.',
      },
    ],
  },
]

type RoutineStep = {
  label: string
  title: string
  text: string
}

function getRawSlide(draft: BeautyPackProps['draft'], index: number) {
  const slides = Array.isArray((draft as any)?.slides) ? (draft as any).slides : []
  return slides[index] || {}
}

function normalizeStep(item: any, index: number): RoutineStep {
  if (typeof item === 'string') {
    return {
      label: `0${index + 1}`,
      title: item.trim(),
      text: '',
    }
  }

  if (item && typeof item === 'object') {
    return {
      label: String(item.label || `0${index + 1}`).trim(),
      title: String(item.title || item.headline || item.text || '').trim(),
      text: String(item.text || item.body || item.description || '').trim(),
    }
  }

  return {
    label: `0${index + 1}`,
    title: '',
    text: '',
  }
}

function normalizeStepsFromSources(...sources: any[]): RoutineStep[] {
  for (const source of sources) {
    if (!Array.isArray(source) || !source.length) continue

    const steps = source
      .map((item, index) => normalizeStep(item, index))
      .filter((item) => item.title || item.text)

    if (steps.length) return steps
  }

  return []
}

function getSlideSteps(
  draft: BeautyPackProps['draft'],
  index: number,
  copy: any,
  fallback: any
): RoutineStep[] {
  const rawSlide = getRawSlide(draft, index)

  return normalizeStepsFromSources(
    rawSlide.steps,
    rawSlide.bullets,
    copy.steps,
    copy.bullets,
    fallback.steps,
    fallback.bullets
  )
}

function getChecklistSteps(draft: BeautyPackProps['draft']): RoutineStep[] {
  const closingRawSlide = getRawSlide(draft, 4)

  const closingSteps = normalizeStepsFromSources(
    closingRawSlide.steps,
    closingRawSlide.bullets,
    fallbacks[4].steps
  )

  if (closingSteps.length) return closingSteps.slice(0, 3)

  const bodySteps = [1, 2, 3]
    .map((slideIndex, idx) => {
      const rawSlide = getRawSlide(draft, slideIndex)
      const title = String(rawSlide?.headline || fallbacks[slideIndex]?.headline || '').trim()
      const text = String(
        rawSlide?.body ||
          rawSlide?.subheadline ||
          fallbacks[slideIndex]?.body ||
          fallbacks[slideIndex]?.subheadline ||
          ''
      ).trim()

      return {
        label: `0${idx + 1}`,
        title,
        text,
      }
    })
    .filter((item) => item.title || item.text)

  return bodySteps.slice(0, 3)
}

function pickChecklistIcon(step: RoutineStep, index: number): LucideIcon {
  const text = `${step.title} ${step.text}`.toLowerCase()

  if (
    text.includes('limpia') ||
    text.includes('clean') ||
    text.includes('suave') ||
    text.includes('barrera')
  ) {
    return Sparkles
  }

  if (
    text.includes('hidrata') ||
    text.includes('hidrat') ||
    text.includes('humedad') ||
    text.includes('moisture') ||
    text.includes('agua')
  ) {
    return Droplets
  }

  if (
    text.includes('spf') ||
    text.includes('solar') ||
    text.includes('protege') ||
    text.includes('protección') ||
    text.includes('protect')
  ) {
    return SunMedium
  }

  if (
    text.includes('calma') ||
    text.includes('natural') ||
    text.includes('piel') ||
    text.includes('skin')
  ) {
    return Leaf
  }

  if (
    text.includes('escudo') ||
    text.includes('daño') ||
    text.includes('mancha')
  ) {
    return Shield
  }

  if (index === 0) return Sparkles
  if (index === 1) return Droplets
  if (index === 2) return SunMedium

  return CheckCircle2
}

function LessonFooterIcon({ index }: { index: number }) {
  const Icon = index === 1 ? Sparkles : index === 2 ? Droplets : SunMedium

  return <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
}

function TopRule({ accent }: { accent: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-px flex-1" style={{ background: `${accent}66` }} />
      <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} strokeWidth={1.6} />
    </div>
  )
}

function LessonSlide({
  draft,
  index,
  accent,
  copy,
  image,
}: {
  draft: BeautyPackProps['draft']
  index: number
  accent: string
  copy: any
  image: string
}) {
  const stepNumber = `0${index}`
  const kicker = copy.eyebrow || `Step ${index}`
  const footer =
    copy.proof ||
    copy.result ||
    (index === 1
      ? 'Suave hoy, saludable mañana.'
      : index === 2
        ? 'El agua no basta. Hay que sellar.'
        : 'SPF hoy, glow mañana.')

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <div className="absolute left-[26px] right-[26px] top-[30px] z-20">
        <div className="flex items-start justify-between">
          <SafeText
            className="text-[44px] font-normal leading-none tracking-[-0.04em]"
            style={{ color: accent, fontFamily: beautySerif }}
          >
            {stepNumber}
          </SafeText>

          <div
            className="rounded-[12px] border px-[12px] py-[5px] text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{
              borderColor: `${accent}66`,
              color: beautyInk,
              fontFamily: beautySans,
            }}
          >
            {kicker}
          </div>
        </div>

        <div className="mt-[16px]">
          <TopRule accent={accent} />
        </div>
      </div>

      <div className="absolute left-[26px] right-[26px] top-[118px] z-20">
        <SafeText
          className="max-w-[360px] text-[47px] font-normal leading-[0.88] tracking-[-0.05em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {copy.headline}
        </SafeText>

        <SafeText
          className="mt-[20px] max-w-[310px] text-[17px] leading-[1.42]"
          style={{
            color: 'rgba(33,24,21,0.75)',
            fontFamily: beautySans,
          }}
        >
          {copy.body || copy.subheadline}
        </SafeText>
      </div>

      <div className="absolute bottom-[54px] left-[22px] right-[22px] z-20 h-[340px] overflow-hidden rounded-t-[24px] rounded-b-none">
        <FullBleedImage
          {...getBeautyVisual(draft, index, image, {
            overlay: 'white',
            opacity: 0.03,
          })}
          objectPosition="center"
        />
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 z-30 flex h-[54px] items-center gap-3 px-[26px] text-[10px] font-bold uppercase tracking-[0.13em]"
        style={{
          background: '#DFDDC9',
          color: 'rgba(33,24,21,0.68)',
          fontFamily: beautySans,
        }}
      >
        <LessonFooterIcon index={index} />
        <SafeText>{footer}</SafeText>
      </div>
    </div>
  )
}

function CoverSlide({
  draft,
  accent,
  copy,
}: {
  draft: BeautyPackProps['draft']
  accent: string
  copy: any
}) {
  return (
    <>
      <FullBleedImage
        {...getBeautyVisual(draft, 0, bg[0], {
          overlay: 'black',
          opacity: 0.18,
        })}
        objectPosition="center"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/22 to-transparent" />

      <div className="absolute left-[34px] top-[174px] z-20 w-[218px]">
        <div className="h-px w-full bg-white/42" />

        <SafeText
          className="my-[9px] text-left text-[14px] uppercase tracking-[0.20em] text-white"
          style={{ fontFamily: beautySans }}
        >
          {copy.eyebrow || 'Skincare class'}
        </SafeText>

        <div className="h-px w-full bg-white/42" />
      </div>

      <div className="absolute bottom-[132px] left-[34px] right-[34px] z-20 text-white">
        <SafeText
          className="max-w-[390px] text-[57px] font-normal leading-[0.92] tracking-[-0.055em]"
          style={{ fontFamily: beautySerif }}
        >
          {copy.headline}
        </SafeText>

        <SafeText
          className="mt-[22px] max-w-[305px] text-[19px] leading-[1.38] text-white/88"
          style={{ fontFamily: beautySans }}
        >
          {copy.subheadline || copy.body}
        </SafeText>
      </div>

      <div
        className="absolute bottom-[34px] left-[36px] z-20 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.15em] text-white"
        style={{ fontFamily: beautySans }}
      >
        <Bookmark className="h-[18px] w-[18px]" strokeWidth={1.8} />
        <span>{copy.cta || 'Guardar rutina'}</span>
      </div>
    </>
  )
}

function ChecklistSlide({
  draft,
  accent,
  copy,
}: {
  draft: BeautyPackProps['draft']
  accent: string
  copy: any
}) {
  const steps = getChecklistSteps(draft)

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <div className="absolute right-[-72px] top-[150px] h-[320px] w-[230px] opacity-[0.10]">
        <Leaf className="h-full w-full" strokeWidth={0.85} style={{ color: accent }} />
      </div>

      <div className="absolute left-[34px] right-[34px] top-[62px] z-20 text-center">
        <SafeText
          className="text-[13px] font-bold uppercase tracking-[0.26em]"
          style={{ color: beautyInk, fontFamily: beautySans }}
        >
          {copy.eyebrow || 'Your glow'}
        </SafeText>

        <SafeText
          className="mt-[10px] text-[55px] font-normal leading-[0.96] tracking-[-0.045em]"
          style={{ color: accent, fontFamily: beautySerif }}
        >
          {copy.headline || 'Checklist'}
        </SafeText>

        <div className="mx-auto mt-[18px] flex max-w-[230px] items-center gap-3">
          <div className="h-px flex-1" style={{ background: `${accent}66` }} />
          <Sparkles className="h-4 w-4" style={{ color: accent }} strokeWidth={1.5} />
          <div className="h-px flex-1" style={{ background: `${accent}66` }} />
        </div>
      </div>

      <div className="absolute left-[58px] right-[42px] top-[226px] z-20 space-y-[28px]">
        {steps.slice(0, 3).map((step, idx) => {
          const Icon = pickChecklistIcon(step, idx)

          return (
            <div key={`${idx}-${step.title}`} className="flex items-start gap-[20px]">
              <div
                className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full"
                style={{ background: '#787946', color: '#fff' }}
              >
                <Icon className="h-[24px] w-[24px]" strokeWidth={1.65} />
              </div>

              <div className="pt-[2px]">
                <SafeText
                  className="text-[18px] font-normal leading-none"
                  style={{ color: beautyInk, fontFamily: beautySerif }}
                >
                  {step.label || `0${idx + 1}`}
                </SafeText>

                <SafeText
                  className="mt-[6px] text-[15px] font-bold leading-[1.18] tracking-[0.03em]"
                  style={{ color: beautyInk, fontFamily: beautySans }}
                >
                  {step.title}
                </SafeText>

                {step.text ? (
                  <SafeText
                    className="mt-[4px] max-w-[270px] text-[14px] leading-[1.34]"
                    style={{ color: 'rgba(33,24,21,0.66)', fontFamily: beautySans }}
                  >
                    {step.text}
                  </SafeText>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>

      <div
        className="absolute bottom-[72px] left-[34px] right-[34px] z-20 rounded-[10px] border p-[14px]"
        style={{ borderColor: `${accent}88` }}
      >
        <div
          className="flex items-center gap-3 rounded-[6px] border px-[16px] py-[14px]"
          style={{ borderColor: `${accent}55` }}
        >
          <Bookmark className="h-[18px] w-[18px]" style={{ color: accent }} strokeWidth={1.7} />

          <SafeText
            className="text-[12px] font-bold uppercase tracking-[0.16em]"
            style={{ color: accent, fontFamily: beautySans }}
          >
            {copy.cta || 'Guardar esta rutina'}
          </SafeText>
        </div>

        <SafeText
          className="mt-[12px] text-center text-[13px] italic leading-[1.35]"
          style={{ color: 'rgba(33,24,21,0.66)', fontFamily: beautySans }}
        >
          {copy.result || copy.body || 'La constancia le gana a la intensidad.'}
        </SafeText>
      </div>
    </div>
  )
}

export default function BeautyEducationalRoutinePack({
  draft,
  activeIndex,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#787946')
  const accent = design.accent || '#787946'

  const copy = getBeautyCopy(draft, index, fallbacks[index])

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 0 ? 'light' : 'dark'}
      className="bg-[#FFF9F0]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <CoverSlide draft={draft} accent={accent} copy={copy} />
      ) : null}

      {index === 1 ? (
        <LessonSlide
          draft={draft}
          index={index}
          accent={accent}
          copy={copy}
          image={bg[1]}
        />
      ) : null}

      {index === 2 ? (
        <LessonSlide
          draft={draft}
          index={index}
          accent={accent}
          copy={copy}
          image={bg[2]}
        />
      ) : null}

      {index === 3 ? (
        <LessonSlide
          draft={draft}
          index={index}
          accent={accent}
          copy={copy}
          image={bg[3]}
        />
      ) : null}

      {index === 4 ? (
        <ChecklistSlide draft={draft} accent={accent} copy={copy} />
      ) : null}
    </BeautyFrame>
  )
}